/**
 * Contact Support form (FR-HELP-06/07/08, AC-SUPPORT-001..006).
 *
 * Rendered in the Help Center overlay's third tab and reachable from the
 * floating help widget on every authenticated page. Email is the only delivery
 * channel — there is no ticket thread to come back to — so the form makes the
 * one-shot nature obvious: it says where the request goes, warns about PII
 * before the user types, and confirms in place on success.
 *
 * Behaviour worth knowing:
 *
 * | Concern            | Decision                                              |
 * | ------------------ | ----------------------------------------------------- |
 * | Recipient missing  | `GET /support/v1/recipient-status` on mount; submit is disabled with the server's own copy, and re-checked immediately before sending in case an admin just configured it. |
 * | Draft safety       | Fields are mirrored into sessionStorage, so a refresh or an accidental overlay close does not lose a long description. |
 * | Validation         | Client-side for instant feedback; the server is authoritative and its per-field 422 errors are mapped back onto the same controls. |
 * | Rate limit (429)   | Rendered as "try again in N minutes" from `Retry-After`. |
 * | Attachment         | One image, ≤ 5 MB, checked before upload with the same limits the service enforces. |
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import type { SupportCategory, SupportRecipientStatus } from '../../lib/api-types';
import { STORAGE_KEYS } from '../../lib/constants';
import { SUPPORT_CATEGORY_OPTIONS } from '../../lib/help-content';

/** FR-HELP-06 limits. Mirrors `services/support/src/constants.ts`. */
export const MAX_SUBJECT_LENGTH = 120;
export const MAX_DESCRIPTION_LENGTH = 4000;
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Microcopy (design.microcopy). Shared with the service so the two agree. */
export const PII_WARNING =
  'Do not paste personally identifiable information or student data in the description field. ' +
  'Support staff will have access to your submission.';
export const RECIPIENT_NOT_CONFIGURED_MESSAGE =
  'Support contact not configured. Please contact your team administrator.';

/**
 * App version reported in the diagnostic bundle. `VITE_APP_VERSION` is injected
 * at build time by CI; the support service substitutes its own `APP_VERSION`
 * when the browser cannot say.
 */
export const APP_VERSION: string =
  typeof import.meta.env.VITE_APP_VERSION === 'string' && import.meta.env.VITE_APP_VERSION !== ''
    ? import.meta.env.VITE_APP_VERSION
    : 'unknown';

/** Counters turn amber here so a long description does not surprise the writer. */
const NEAR_LIMIT_RATIO = 0.9;

interface DraftState {
  category: SupportCategory;
  subject: string;
  description: string;
}

const EMPTY_DRAFT: DraftState = { category: 'bug', subject: '', description: '' };

function readDraft(): DraftState {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEYS.supportDraft);
    if (raw === null) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw) as Partial<DraftState>;
    const isKnownCategory = SUPPORT_CATEGORY_OPTIONS.some(
      (option) => option.value === parsed.category
    );
    return {
      category: isKnownCategory ? (parsed.category as SupportCategory) : EMPTY_DRAFT.category,
      subject: typeof parsed.subject === 'string' ? parsed.subject : '',
      description: typeof parsed.description === 'string' ? parsed.description : '',
    };
  } catch {
    // Private-mode storage or corrupted JSON: an empty form beats a crash.
    return EMPTY_DRAFT;
  }
}

function writeDraft(draft: DraftState): void {
  try {
    if (draft.subject === '' && draft.description === '') {
      window.sessionStorage.removeItem(STORAGE_KEYS.supportDraft);
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEYS.supportDraft, JSON.stringify(draft));
  } catch {
    // Draft persistence is a convenience, never a blocker.
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "Too many support requests. Please try again in 42 minutes." */
export function rateLimitMessage(retryAfterSeconds: number): string {
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  return `Too many support requests. Please try again in ${minutes} minute${
    minutes === 1 ? '' : 's'
  }.`;
}

type FieldErrors = Partial<Record<'category' | 'subject' | 'description' | 'attachment', string>>;

/** Client-side mirror of the server rules — fast feedback, same limits. */
function validateDraft(draft: DraftState): FieldErrors {
  const errors: FieldErrors = {};
  if (draft.subject.trim() === '') errors.subject = 'Enter a subject.';
  else if (draft.subject.trim().length > MAX_SUBJECT_LENGTH) {
    errors.subject = `Must be ${MAX_SUBJECT_LENGTH} characters or fewer.`;
  }
  if (draft.description.trim() === '') errors.description = 'Describe the problem.';
  else if (draft.description.trim().length > MAX_DESCRIPTION_LENGTH) {
    errors.description = `Must be ${MAX_DESCRIPTION_LENGTH} characters or fewer.`;
  }
  return errors;
}

function validateAttachment(file: File | null): string | null {
  if (file === null) return null;
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return 'Attachments must be an image (PNG, JPEG, GIF or WebP).';
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `Attachments must be 5 MB or smaller. That file is ${formatBytes(file.size)}.`;
  }
  return null;
}

/** Map an RFC 7807 `details.fields` array back onto the controls that own them. */
function toFieldErrors(error: ApiError): FieldErrors {
  const raw = error.details?.fields;
  if (!Array.isArray(raw)) return {};
  const errors: FieldErrors = {};
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as { field?: unknown; message?: unknown };
    if (typeof record.field !== 'string' || typeof record.message !== 'string') continue;
    if (
      record.field === 'category' ||
      record.field === 'subject' ||
      record.field === 'description' ||
      record.field === 'attachment'
    ) {
      errors[record.field] = record.message;
    }
  }
  return errors;
}

export function ContactSupportForm(): JSX.Element {
  const apiClient = useApiClient();
  const { persona } = useSession();
  const [draft, setDraft] = useState<DraftState>(() => readDraft());
  const [attachment, setAttachment] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [recipient, setRecipient] = useState<SupportRecipientStatus | null>(null);
  const [isLoadingRecipient, setIsLoadingRecipient] = useState(true);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  const loadRecipient = useCallback(
    async (signal?: AbortSignal): Promise<SupportRecipientStatus | null> => {
      try {
        const status = await apiClient.getSupportRecipientStatus(signal);
        setRecipient(status);
        setRecipientError(null);
        return status;
      } catch (cause) {
        if (signal?.aborted === true) return null;
        setRecipientError(
          ApiError.isApiError(cause) && cause.isOffline
            ? 'You are offline. Reconnect to send a support request.'
            : 'We could not check the support contact. Try again in a moment.'
        );
        return null;
      } finally {
        setIsLoadingRecipient(false);
      }
    },
    [apiClient]
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadRecipient(controller.signal);
    return () => controller.abort();
  }, [loadRecipient]);

  useEffect(() => {
    writeDraft(draft);
  }, [draft]);

  const isConfigured = recipient?.is_configured === true;
  const isDisabled = isSubmitting || isLoadingRecipient || !isConfigured;
  const disabledReason = useMemo(() => {
    if (isLoadingRecipient) return 'Checking where support requests go…';
    if (recipientError !== null) return recipientError;
    if (!isConfigured) return recipient?.message ?? RECIPIENT_NOT_CONFIGURED_MESSAGE;
    return null;
  }, [isConfigured, isLoadingRecipient, recipient, recipientError]);

  function patchDraft(changes: Partial<DraftState>): void {
    setDraft((current) => ({ ...current, ...changes }));
    setSubmittedId(null);
  }

  function onPickFile(file: File | null): void {
    const error = validateAttachment(file);
    setFieldErrors((current) => ({ ...current, attachment: error ?? undefined }));
    setAttachment(error === null ? file : null);
    if (error !== null && fileInputRef.current !== null) fileInputRef.current.value = '';
  }

  function clearAttachment(): void {
    setAttachment(null);
    setFieldErrors((current) => ({ ...current, attachment: undefined }));
    if (fileInputRef.current !== null) fileInputRef.current.value = '';
  }

  async function submit(): Promise<void> {
    const errors = validateDraft(draft);
    const attachmentError = validateAttachment(attachment);
    if (attachmentError !== null) errors.attachment = attachmentError;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      subjectRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    try {
      // Re-check: a Platform Admin may have configured the address since mount.
      const latest = await loadRecipient();
      if (latest !== null && !latest.is_configured) {
        setFormError(latest.message ?? RECIPIENT_NOT_CONFIGURED_MESSAGE);
        return;
      }

      const created = await apiClient.submitSupportRequest({
        category: draft.category,
        subject: draft.subject.trim(),
        description: draft.description.trim(),
        diagnostic_bundle: {
          current_url: window.location.href,
          app_version: APP_VERSION,
          browser_user_agent: window.navigator.userAgent,
        },
        attachment,
      });

      setSubmittedId(created.id);
      setDraft(EMPTY_DRAFT);
      clearAttachment();
      writeDraft(EMPTY_DRAFT);
    } catch (cause) {
      if (!ApiError.isApiError(cause)) {
        setFormError('We could not send that request. Please try again.');
        return;
      }
      if (cause.status === 429) {
        const retryAfter = Number(cause.details?.retry_after_seconds ?? 0);
        setFormError(rateLimitMessage(Number.isFinite(retryAfter) ? retryAfter : 3600));
        return;
      }
      if (cause.code === 'SUPPORT_RECIPIENT_NOT_CONFIGURED') {
        setFormError(RECIPIENT_NOT_CONFIGURED_MESSAGE);
        void loadRecipient();
        return;
      }
      if (cause.status === 422 || cause.status === 413 || cause.status === 415) {
        const mapped = toFieldErrors(cause);
        setFieldErrors(mapped);
        setFormError(Object.keys(mapped).length > 0 ? null : cause.message);
        return;
      }
      setFormError(
        cause.isOffline
          ? 'You are offline. Your request was not sent — try again once you reconnect.'
          : cause.message
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const subjectLength = draft.subject.length;
  const descriptionLength = draft.description.length;

  return (
    <section className="support-form" aria-labelledby="support-form-title">
      <h3 className="support-form__title" id="support-form-title">
        Contact Support
      </h3>
      <p className="support-form__lede">
        Support replies by email — there is no ticket thread inside the app. Include what you
        expected and what happened instead.
      </p>

      <p className="banner banner--warning support-form__pii" role="note">
        {PII_WARNING}
      </p>

      {disabledReason !== null && !isLoadingRecipient ? (
        <p className="banner banner--error" role="alert" data-testid="support-disabled-reason">
          {disabledReason}
        </p>
      ) : null}
      {formError !== null ? (
        <p className="banner banner--error" role="alert">
          {formError}
        </p>
      ) : null}
      {submittedId !== null ? (
        <p className="banner banner--success" role="status">
          Request sent. Support has your details and will reply by email.
        </p>
      ) : null}

      {/* Subject/description survive navigation via the sessionStorage draft;
          the picked file is the one thing that cannot be persisted. */}
      <UnsavedChangesGuard
        isDirty={attachment !== null && !isSubmitting}
        message="Your attached file is not kept with the draft — leaving this page removes it. Your subject and description are saved."
      />
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="support-category">
            Category<span aria-hidden="true"> *</span>
            <span className="visually-hidden"> (required)</span>
          </label>
          <select
            className="field__control"
            id="support-category"
            value={draft.category}
            onChange={(event) => patchDraft({ category: event.target.value as SupportCategory })}
          >
            {SUPPORT_CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="field__hint">
            {SUPPORT_CATEGORY_OPTIONS.find((option) => option.value === draft.category)?.hint}
          </span>
        </div>

        <div className={`field${fieldErrors.subject === undefined ? '' : ' field--invalid'}`}>
          <label className="field__label" htmlFor="support-subject">
            Subject<span aria-hidden="true"> *</span>
            <span className="visually-hidden"> (required)</span>
          </label>
          <input
            className="field__control"
            id="support-subject"
            ref={subjectRef}
            maxLength={MAX_SUBJECT_LENGTH}
            value={draft.subject}
            aria-invalid={fieldErrors.subject === undefined ? undefined : true}
            aria-describedby="support-subject-count"
            onChange={(event) => patchDraft({ subject: event.target.value })}
            placeholder="Play entry drops the third-down flag"
          />
          <span
            className={`support-form__count${
              subjectLength > MAX_SUBJECT_LENGTH * NEAR_LIMIT_RATIO
                ? ' support-form__count--near'
                : ''
            }`}
            id="support-subject-count"
          >
            {subjectLength} / {MAX_SUBJECT_LENGTH}
          </span>
          {fieldErrors.subject === undefined ? null : (
            <span className="field__error" role="alert">
              {fieldErrors.subject}
            </span>
          )}
        </div>

        <div className={`field${fieldErrors.description === undefined ? '' : ' field--invalid'}`}>
          <label className="field__label" htmlFor="support-description">
            What happened?<span aria-hidden="true"> *</span>
            <span className="visually-hidden"> (required)</span>
          </label>
          <textarea
            className="field__control support-form__textarea"
            id="support-description"
            rows={6}
            maxLength={MAX_DESCRIPTION_LENGTH}
            value={draft.description}
            aria-invalid={fieldErrors.description === undefined ? undefined : true}
            aria-describedby="support-description-count"
            onChange={(event) => patchDraft({ description: event.target.value })}
            placeholder="Plain text only. Steps you took, what you expected, what you saw."
          />
          <span
            className={`support-form__count${
              descriptionLength > MAX_DESCRIPTION_LENGTH * NEAR_LIMIT_RATIO
                ? ' support-form__count--near'
                : ''
            }`}
            id="support-description-count"
          >
            {descriptionLength} / {MAX_DESCRIPTION_LENGTH}
          </span>
          {fieldErrors.description === undefined ? null : (
            <span className="field__error" role="alert">
              {fieldErrors.description}
            </span>
          )}
        </div>

        <div className={`field${fieldErrors.attachment === undefined ? '' : ' field--invalid'}`}>
          <label className="field__label" htmlFor="support-attachment">
            Screenshot (optional)
          </label>
          <input
            className="field__control"
            id="support-attachment"
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            onChange={(event) => onPickFile(event.target.files?.[0] ?? null)}
          />
          <span className="field__hint">One image, PNG/JPEG/GIF/WebP, up to 5 MB.</span>
          {attachment === null ? null : (
            <span className="support-form__file" data-testid="support-attachment-chip">
              {attachment.name} · {formatBytes(attachment.size)}
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={clearAttachment}
                aria-label={`Remove attachment ${attachment.name}`}
              >
                Remove
              </button>
            </span>
          )}
          {fieldErrors.attachment === undefined ? null : (
            <span className="field__error" role="alert">
              {fieldErrors.attachment}
            </span>
          )}
        </div>

        <details className="support-form__diagnostics">
          <summary>What we attach automatically</summary>
          <p>
            Technical context only — the page you are on, the app version, your browser and your
            role. No player, roster or student data is included.
          </p>
          <ul>
            <li>Page: {window.location.pathname}</li>
            <li>Role: {persona ?? 'unknown'}</li>
            <li>App version: {APP_VERSION}</li>
          </ul>
        </details>

        <div className="support-form__actions">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={isDisabled}
            title={disabledReason ?? undefined}
          >
            {isSubmitting ? 'Sending…' : 'Send request'}
          </button>
          {recipient?.rate_limit.remaining === null ||
          recipient?.rate_limit.remaining === undefined ? null : (
            <span className="support-form__quota">
              {recipient.rate_limit.remaining} of {recipient.rate_limit.user_hourly_limit} requests
              left this hour
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
